import { Controller } from '@hotwired/stimulus'
import { get } from '@rails/request.js'
import _ from 'lodash'

export default class extends Controller {
  static targets = ['input', 'results', 'selectedId', 'submit', 'error', 'spinner']

  initialize() {
    this.searchDebounced = _.debounce(this.performSearch.bind(this), 300)
  }

  connect() {
    console.log('PlaintiffSearch controller connected')
    this.clickOutsideHandler = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.clickOutsideHandler)
  }

  disconnect() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
    }
    if (this.searchDebounced) {
      this.searchDebounced.cancel()
    }
  }

  search(event) {
    if (!event.target) return

    const query = event.target.value.trim()
    this.selectedIdTarget.value = ''
    this.clearError()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.showSpinner()
    this.searchDebounced(query)
  }

  async performSearch(query) {
    if (!query) return

    try {
      const response = await get(`/api/plaintiffs/search?query=${encodeURIComponent(query)}`, {
        responseKind: 'json',
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json
      this.renderResults(data.results)
    } catch (error) {
      console.error('Search failed:', error)
      this.showError('Failed to search. Please try again.')
      this.hideResults()
    } finally {
      this.hideSpinner()
    }
  }

  renderResults(results) {
    if (!results || !Array.isArray(results) || results.length === 0) {
      this.hideResults()
      return
    }

    try {
      this.resultsTarget.innerHTML = results
        .map(
          result => `
        <div class="search-result p-2 cursor-pointer hover-bg-light" 
             data-action="click->plaintiff-search#selectResult"
             data-id="${this.escapeHtml(result.id)}"
             data-name="${this.escapeHtml(result.name)}">
          <div class="fw-medium">${this.escapeHtml(result.name)}</div>
        </div>
      `
        )
        .join('')

      this.showResults()
    } catch (error) {
      console.error('Error rendering results:', error)
      this.showError('Failed to display results. Please try again.')
      this.hideResults()
    }
  }

  selectResult(event) {
    if (!event.currentTarget) return

    try {
      const { id, name } = event.currentTarget.dataset

      if (!id || !name) {
        throw new Error('Invalid selection data')
      }

      this.inputTarget.value = name
      this.selectedIdTarget.value = id
      this.hideResults()
      this.clearError()
    } catch (error) {
      console.error('Error selecting result:', error)
      this.showError('Failed to select plaintiff. Please try again.')
    }
  }

  handleClickOutside(event) {
    if (!event.target) return

    if (!this.resultsTarget.contains(event.target) && !this.inputTarget.contains(event.target)) {
      this.hideResults()
    }
  }

  // Helper methods
  showResults() {
    this.resultsTarget.classList.add('show')
  }

  hideResults() {
    this.resultsTarget.classList.remove('show')
  }

  showSpinner() {
    this.spinnerTarget.classList.remove('d-none')
  }

  hideSpinner() {
    this.spinnerTarget.classList.add('d-none')
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.inputTarget.classList.add('is-invalid')
  }

  clearError() {
    this.errorTarget.textContent = ''
    this.inputTarget.classList.remove('is-invalid')
  }

  escapeHtml(unsafe) {
    if (typeof unsafe !== 'string' && typeof unsafe !== 'number') return ''

    return String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }
}
